import { 
  db, 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  addDoc, 
  updateDoc,
  deleteDoc,
  query, 
  orderBy,
  auth
} from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

// Products functions
export const fetchProducts = async () => {
  try {
    const productsRef = collection(db, 'products');
    const snapshot = await getDocs(productsRef);
    const products = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    console.log('Fetched products from Firestore:', products.length);
    return products;
  } catch (error) {
    console.error('Error fetching products:', error);
    // Return empty array instead of mock data
    console.warn('Returning empty products array due to error');
    return [];
  }
};

// Helper function to wait for auth state
const waitForAuth = () => {
  return new Promise((resolve, reject) => {
    // If user is already authenticated, resolve immediately
    if (auth.currentUser) {
      console.log('User already authenticated:', auth.currentUser.email);
      resolve(auth.currentUser);
      return;
    }
    
    // Otherwise, wait for auth state change
    let unsubscribe;
    const timeout = setTimeout(() => {
      if (unsubscribe) unsubscribe();
      reject(new Error('Authentication timeout - user not logged in'));
    }, 5000); // 5 second timeout
    
    unsubscribe = onAuthStateChanged(auth, (user) => {
      clearTimeout(timeout);
      if (unsubscribe) unsubscribe();
      if (user) {
        console.log('User authenticated via listener:', user.email);
        resolve(user);
      } else {
        reject(new Error('User not authenticated'));
      }
    }, (error) => {
      clearTimeout(timeout);
      if (unsubscribe) unsubscribe();
      reject(error);
    });
  });
};

// Delete product function
export const deleteProduct = async (productId) => {
  try {
    if (!productId) {
      throw new Error('Product ID is required');
    }

    // Wait for authentication to be ready
    console.log('Checking authentication...');
    let currentUser;
    try {
      currentUser = await waitForAuth();
      console.log('User authenticated:', currentUser.email);
    } catch (authError) {
      console.error('Authentication error:', authError);
      throw new Error('You must be logged in to delete products. Please refresh the page and log in again.');
    }

    if (!currentUser) {
      throw new Error('You must be logged in to delete products. Please refresh the page and log in again.');
    }

    console.log('Attempting to delete product:', productId);
    console.log('Current user:', currentUser.email);
    console.log('User ID:', currentUser.uid);

    // First check if the document exists
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);
    
    if (!productSnap.exists()) {
      throw new Error('Product not found. It may have already been deleted.');
    }

    console.log('Product exists, attempting deletion...');
    
    // Delete the document
    await deleteDoc(productRef);
    console.log('Product deleted successfully:', productId);
    return true;
  } catch (error) {
    console.error('Error deleting product:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Full error:', error);
    
    // Provide more specific error messages
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied. Please check Firebase security rules and ensure you are logged in. Error details: ' + error.message);
    } else if (error.code === 'not-found') {
      throw new Error('Product not found. It may have already been deleted.');
    } else if (error.message) {
      throw error;
    } else {
      throw new Error(`Failed to delete product: ${error.message || 'Unknown error'}`);
    }
  }
};

// Update product function
export const updateProduct = async (productId, productData) => {
  try {
    const productRef = doc(db, 'products', productId);
    await updateDoc(productRef, productData);
    return true;
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

// Add product function
export const addProduct = async (productData) => {
  try {
    console.log('=== ADD PRODUCT DEBUG ===');
    console.log('Product data:', productData);
    console.log('Current auth state:', auth.currentUser);
    
    // Check authentication
    if (!auth.currentUser) {
      throw new Error('User not authenticated. Please log in and try again.');
    }
    
    console.log('User email:', auth.currentUser.email);
    console.log('User ID:', auth.currentUser.uid);
    
    // Convert Date objects to Firestore Timestamp if needed
    const processedData = { ...productData };
    if (processedData.createdAt instanceof Date) {
      processedData.createdAt = processedData.createdAt.toISOString();
    }
    if (processedData.updatedAt instanceof Date) {
      processedData.updatedAt = processedData.updatedAt.toISOString();
    }
    
    console.log('Processed data:', processedData);
    
    const productsRef = collection(db, 'products');
    console.log('Adding document to Firestore...');
    const docRef = await addDoc(productsRef, processedData);
    console.log('Product added successfully with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('=== ERROR ADDING PRODUCT ===');
    console.error('Error object:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    throw error;
  }
};

export const fetchProductById = async (productId) => {
  try {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);
    
    if (productSnap.exists()) {
      return { id: productSnap.id, ...productSnap.data() };
    } else {
      throw new Error('Product not found');
    }
  } catch (error) {
    console.error('Error fetching product:', error);
    // Return null instead of mock data
    return null;
  }
};

// Blog functions
export const fetchBlogPosts = async () => {
  try {
    const blogsRef = collection(db, 'blogs');
    const q = query(blogsRef, orderBy('publishedAt', 'desc'));
    const snapshot = await getDocs(q);
    const blogs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    console.log('Fetched blogs from Firestore:', blogs.length);
    return blogs;
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    // Return empty array instead of mock data
    console.warn('Returning empty blogs array due to error');
    return [];
  }
};

export const fetchBlogPostById = async (blogId) => {
  try {
    const blogRef = doc(db, 'blogs', blogId);
    const blogSnap = await getDoc(blogRef);
    
    if (blogSnap.exists()) {
      return { id: blogSnap.id, ...blogSnap.data() };
    } else {
      throw new Error('Blog post not found');
    }
  } catch (error) {
    console.error('Error fetching blog post:', error);
    // Return null instead of mock data
    return null;
  }
};

// Delete blog function
export const deleteBlog = async (blogId) => {
  try {
    if (!blogId) {
      throw new Error('Blog ID is required');
    }

    // Wait for authentication to be ready
    console.log('Checking authentication...');
    let currentUser;
    try {
      currentUser = await waitForAuth();
      console.log('User authenticated:', currentUser.email);
    } catch (authError) {
      console.error('Authentication error:', authError);
      throw new Error('You must be logged in to delete blog posts. Please refresh the page and log in again.');
    }

    if (!currentUser) {
      throw new Error('You must be logged in to delete blog posts. Please refresh the page and log in again.');
    }

    console.log('Attempting to delete blog:', blogId);
    console.log('Current user:', currentUser.email);
    console.log('User ID:', currentUser.uid);

    // First check if the document exists
    const blogRef = doc(db, 'blogs', blogId);
    const blogSnap = await getDoc(blogRef);
    
    if (!blogSnap.exists()) {
      throw new Error('Blog post not found. It may have already been deleted.');
    }

    console.log('Blog exists, attempting deletion...');
    
    // Delete the document
    await deleteDoc(blogRef);
    console.log('Blog deleted successfully:', blogId);
    return true;
  } catch (error) {
    console.error('Error deleting blog:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Full error:', error);
    
    // Provide more specific error messages
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied. Please check Firebase security rules and ensure you are logged in. Error details: ' + error.message);
    } else if (error.code === 'not-found') {
      throw new Error('Blog post not found. It may have already been deleted.');
    } else if (error.message) {
      throw error;
    } else {
      throw new Error(`Failed to delete blog post: ${error.message || 'Unknown error'}`);
    }
  }
};

// Update blog function
export const updateBlog = async (blogId, blogData) => {
  try {
    const blogRef = doc(db, 'blogs', blogId);
    await updateDoc(blogRef, blogData);
    return true;
  } catch (error) {
    console.error('Error updating blog:', error);
    throw error;
  }
};

// Add blog function
export const addBlog = async (blogData) => {
  try {
    const blogsRef = collection(db, 'blogs');
    const docRef = await addDoc(blogsRef, blogData);
    return docRef.id;
  } catch (error) {
    console.error('Error adding blog:', error);
    throw error;
  }
};

// Contact form function
export const submitContactMessage = async (formData) => {
  try {
    const contactRef = collection(db, 'contactMessages');
    await addDoc(contactRef, {
      ...formData,
      createdAt: new Date(),
      isRead: false
    });
    return true;
  } catch (error) {
    console.error('Error submitting contact message:', error);
    // Simulate success for fallback
    console.log('Contact message would be submitted:', formData);
    return true;
  }
};

// Categories
export const getProductCategories = () => [
  { id: 'all', name: 'All Products' },
  { id: 'fertilizers', name: 'Fertilizers' },
  { id: 'seeds', name: 'Seeds' },
  { id: 'plants', name: 'Plants' },
  { id: 'tools', name: 'Tools' },
  { id: 'soil-mix', name: 'Soil Mix' },
  { id: 'decoratives', name: 'Decoratives' },
  { id: 'other', name: 'Other' }
];

export const getBlogCategories = () => [
  { id: 'all', name: 'All Articles' },
  { id: 'gardening-tips', name: 'Gardening Tips' },
  { id: 'fertilizers', name: 'Fertilizers' },
  { id: 'seasonal', name: 'Seasonal Guide' },
  { id: 'composting', name: 'Composting' },
  { id: 'plant-care', name: 'Plant Care' },
  { id: 'container-gardening', name: 'Container Gardening' }
];
